import { useLocation } from '@reach/router'
import { DemoScheduler } from 'components/DemoScheduler'
import useiCloudReady from '../../hooks/useiCloudReady'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import HubspotForm from 'react-hubspot-form'
import { Browser, Chat } from 'components/Icons/Icons'

export default function Contact(props) {
    const icloudready = useiCloudReady()
    const location = useLocation()
    const [activeTab, setActiveTab] = useState(props.activeTab || 'demo')
    const demoCalenderUrl = props.demoCalenderUrl || 'https://calendly.com/icloudready/request-discovery'
    const demoCalenderTitle = props.demoCalenderTitle || 'Book a demo'
    const qaCalenderTitle = props.qaCalenderTitle || 'Book 30-min chat'
    const qaCalenderUrl = props.qaCalenderUrl || "https://calendly.com/icloudready/icloudready-30-minute-quick-chat"
    const title = props.title || 'Book a Meeting'
    const subtitle = props.subtitle || ''
    const description = props.description || 'Choose the option that works best for you'
    
    const handleChipClick = (tab) => {
        setActiveTab(tab)
        icloudready?.capture(`contact: clicked ${tab} button`)
        if (typeof window !== 'undefined') {
            window.location.hash = `#${tab}`
        }
    }

    useEffect(() => {
        const tab = location.hash.replace('#', '')
        const demo = queryString.parse(location.search)?.demo
        if (tab === 'contact' || tab === 'demo' || tab === 'qa') {
            setActiveTab(tab)
        }
    }, [location])

    const tabs = [
        {
            id: 'demo',
            label: demoCalenderTitle,
            description: 'Get a personalized walkthrough of our platform',
            icon: Browser,
        },
        {
            id: 'qa',
            label: qaCalenderTitle,
            description: 'Quick chat to answer your questions',
            icon: Chat,
        },
    ]

    return (
        <div className="w-full relative">
            {/* Background decorative elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-red/5 dark:bg-red/10 rounded-full blur-3xl"></div>
                <div className="absolute top-1/4 right-0 w-[400px] h-[400px] bg-red/3 dark:bg-red/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-red/3 dark:bg-red/5 rounded-full blur-3xl"></div>
            </div>

            {/* Header Section */}
            <div className="text-center mb-16 relative z-10">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-primary dark:text-primary-dark tracking-tight">
                    {title}
                </h1>
                {subtitle && (
                    <h2 className="text-xl md:text-2xl font-semibold mb-4 text-primary/70 dark:text-primary-dark/70">
                        {subtitle}
                    </h2>
                )}
                {description && (
                    <p className="text-lg md:text-xl text-primary/60 dark:text-primary-dark/60 max-w-2xl mx-auto leading-relaxed">
                        {description}
                    </p>
                )}
            </div>

            {/* Tabs Section */}
            {!props.hideTabs && (
                <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-4 mb-16 relative z-10">
                    {tabs.map((tab) => {
                        const Icon = tab.icon
                        const isActive = activeTab === tab.id
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleChipClick(tab.id)}
                                className={`
                                    group relative
                                    w-full sm:w-auto sm:min-w-[280px]
                                    px-8 py-6
                                    rounded-2xl
                                    border-2
                                    transition-all duration-500 ease-out
                                    transform hover:scale-[1.02] active:scale-[0.98]
                                    overflow-hidden
                                    ${
                                        isActive
                                            ? 'bg-gradient-to-br from-red to-red/90 border-red text-white shadow-2xl shadow-red/30 scale-[1.02]'
                                            : 'bg-white/80 dark:bg-gray-accent-dark/80 backdrop-blur-sm border-gray-accent-light dark:border-gray-accent-light hover:border-red/50 text-primary dark:text-primary-dark hover:shadow-xl'
                                    }
                                `}
                            >
                                {/* Animated background gradient on hover */}
                                {!isActive && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-red/0 to-red/0 group-hover:from-red/5 group-hover:to-red/10 transition-all duration-500 rounded-2xl"></div>
                                )}
                                
                                {/* Active indicator glow */}
                                {isActive && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
                                )}

                                <div className="relative flex flex-col items-center space-y-3">
                                    <div className={`
                                        p-3 rounded-xl transition-all duration-300
                                        ${isActive 
                                            ? 'bg-white/20 text-white' 
                                            : 'bg-gray-accent-light dark:bg-gray-accent-light/50 text-primary dark:text-primary-dark group-hover:bg-red/10 group-hover:text-red'
                                        }
                                    `}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <span className={`font-bold text-lg transition-colors duration-300 ${isActive ? 'text-white' : ''}`}>
                                            {tab.label}
                                        </span>
                                        <span
                                            className={`text-sm mt-2 transition-colors duration-300 ${
                                                isActive
                                                    ? 'text-white/90'
                                                    : 'text-primary/60 dark:text-primary-dark/60'
                                            }`}
                                        >
                                            {tab.description}
                                        </span>
                                    </div>
                                </div>

                                {/* Bottom accent line */}
                                {isActive && (
                                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-white/50 rounded-full"></div>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Content Section with fade animation */}
            <div className="mt-8 relative z-10">
                {activeTab === 'contact' ? (
                    <div key="contact" className="max-w-xl mx-auto mt-12 animate-fadeIn">
                        <div className="bg-white dark:bg-gray-accent-dark rounded-2xl shadow-xl p-8 border border-gray-accent-light dark:border-gray-accent-light">
                            <HubspotForm
                                portalId="39517643"
                                formId="82bf274d-76ea-4cc4-b1ef-50066538de0e"
                                onSubmit={() => {}}
                            />
                        </div>
                    </div>
                ) : null}
                {activeTab === 'demo' ? (
                    <div key="demo" className="max-w-5xl mx-auto animate-fadeIn">
                        <div className="relative group">
                            {/* Glow effect */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-red/20 to-red/10 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                            <div className="relative bg-white dark:bg-gray-accent-dark rounded-2xl shadow-2xl p-4 md:p-8 border border-gray-accent-light dark:border-gray-accent-light backdrop-blur-sm">
                                <DemoScheduler
                                    type="demo"
                                    iframeSrc={demoCalenderUrl}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
                {activeTab === 'qa' ? (
                    <div key="qa" className="max-w-5xl mx-auto animate-fadeIn">
                        <div className="relative group">
                            {/* Glow effect */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-red/20 to-red/10 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                            <div className="relative bg-white dark:bg-gray-accent-dark rounded-2xl shadow-2xl p-4 md:p-8 border border-gray-accent-light dark:border-gray-accent-light backdrop-blur-sm">
                                <DemoScheduler
                                    type="qa"
                                    iframeSrc={qaCalenderUrl}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}
